import React, { useState, useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';

/**
 * Structured Image Component
 * Renders images with optional captions, lightbox, and loading states
 */
const StructuredImage = ({
  src,
  alt = '',
  title,
  caption,
  width,
  height,
  maxWidth = '100%',
  maxHeight = '400px',
  lightbox = false,
  className,
  style = {},
}) => {
  const { theme } = useAppTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleImageLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError(true);
  };

  const handleImageClick = () => {
    if (lightbox && !error) {
      setLightboxOpen(true);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const handleLightboxClick = (e) => {
    if (e.target === e.currentTarget) {
      closeLightbox();
    }
  };

  const styles = {
    container: {
      backgroundColor: theme.colors.background.sidebar,
      borderRadius: theme.borderRadius.md,
      border: `1px solid ${theme.colors.border}`,
      overflow: 'hidden',
      ...style,
    },
    imageContainer: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.background.main,
      minHeight: '100px',
    },
    image: {
      maxWidth: maxWidth,
      maxHeight: maxHeight,
      width: width || 'auto',
      height: height || 'auto',
      objectFit: 'contain',
      cursor: lightbox ? 'pointer' : 'default',
      transition: 'transform 0.2s',
    },
    imageHover: {
      transform: 'scale(1.02)',
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100px',
      color: theme.colors.text.muted,
      fontSize: '14px',
    },
    errorContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100px',
      color: theme.colors.text.muted,
      fontSize: '14px',
      padding: theme.spacing.md,
      textAlign: 'center',
    },
    errorIcon: {
      fontSize: '24px',
      marginBottom: theme.spacing.sm,
    },
    content: {
      padding: theme.spacing.md,
    },
    title: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme.colors.text.primary,
      margin: 0,
      marginBottom: caption ? theme.spacing.sm : 0,
    },
    caption: {
      fontSize: '14px',
      color: theme.colors.text.secondary,
      margin: 0,
      lineHeight: '1.5',
    },
    lightboxOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme.colors.overlayDark,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      cursor: 'pointer',
    },
    lightboxImage: {
      maxWidth: '90vw',
      maxHeight: '90vh',
      objectFit: 'contain',
      cursor: 'default',
    },
    lightboxClose: {
      position: 'absolute',
      top: theme.spacing.lg,
      right: theme.spacing.lg,
      color: theme.colors.text.inverse,
      fontSize: '24px',
      cursor: 'pointer',
      backgroundColor: theme.colors.overlayDark,
      border: 'none',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    lightboxCloseHover: {
      backgroundColor: theme.colors.overlayDark,
    },
  };

  if (!src) {
    return (
      <div style={styles.container} className={className}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>🖼️</div>
          <div>No image source provided</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={styles.container} className={className}>
        <div style={styles.imageContainer}>
          {loading && <div style={styles.loadingContainer}>Loading image...</div>}

          {error && (
            <div style={styles.errorContainer}>
              <div style={styles.errorIcon}>❌</div>
              <div>Failed to load image</div>
              <div style={{ fontSize: '12px', marginTop: theme.spacing.xs }}>{src}</div>
            </div>
          )}

          {!error && (
            <img
              src={src}
              alt={alt}
              style={{
                ...styles.image,
                display: loading ? 'none' : 'block',
              }}
              onLoad={handleImageLoad}
              onError={handleImageError}
              onClick={handleImageClick}
              onMouseEnter={(e) => {
                if (lightbox) {
                  e.target.style.transform = 'scale(1.02)';
                }
              }}
              onMouseLeave={(e) => {
                if (lightbox) {
                  e.target.style.transform = 'scale(1)';
                }
              }}
            />
          )}
        </div>

        {(title || caption) && (
          <div style={styles.content}>
            {title && <h3 style={styles.title}>{title}</h3>}
            {caption && <p style={styles.caption}>{caption}</p>}
          </div>
        )}
      </div>

      {lightboxOpen && !error && (
        <div style={styles.lightboxOverlay} onClick={handleLightboxClick}>
          <button
            style={styles.lightboxClose}
            onClick={closeLightbox}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.overlayDark;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.overlayDark;
            }}
          >
            ✕
          </button>
          <img src={src} alt={alt} style={styles.lightboxImage} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

export default StructuredImage;
